import { useEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import type LenisType from "lenis";
import type gsapType from "gsap";
import type { ScrollTrigger as ScrollTriggerType } from "gsap/ScrollTrigger";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { scrollToElement } from "@/lib/scrollToElement";

type SmoothScrollProviderProps = {
  children: ReactNode;
};

const HEADER_OFFSET = 90; // sticky header (~5rem) so a targeted section title clears it.

/** Same gate the Lenis effect uses, smooth scroll only on capable desktop devices. */
function smoothScrollSupported(reducedMotion: boolean) {
  if (reducedMotion || typeof window === "undefined") {
    return false;
  }
  const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const narrowViewport = window.innerWidth < 1024;
  const saveData =
    "connection" in navigator &&
    Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData);
  const deviceMemory =
    "deviceMemory" in navigator
      ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4
      : 4;
  return !(coarsePointer || narrowViewport || saveData || deviceMemory < 4);
}

/**
 * Initialise Lenis inertia + GSAP ScrollTrigger and, when the page opened on a deep
 * link (`/page#section`), settle onto the target once before revealing. Runs ONLY on
 * capable desktops, and only after its libraries have been dynamically imported, so
 * Lenis + gsap never touch the mobile critical path. Returns a teardown function.
 */
function setupSmoothScroll(
  Lenis: typeof LenisType,
  gsap: typeof gsapType,
  ScrollTrigger: typeof ScrollTriggerType,
  setCovering: Dispatch<SetStateAction<boolean>>,
): () => void {
  gsap.registerPlugin(ScrollTrigger);

  // Cinematic inertia: longer settle with a confident ease-out tail.
  const lenis = new Lenis({
    duration: 1.05,
    easing: (t: number) => 1 - Math.pow(1 - t, 4),
    smoothWheel: true,
    syncTouch: false,
    wheelMultiplier: 1,
    touchMultiplier: 1,
  });

  lenis.on("scroll", ScrollTrigger.update);

  // Expose the active Lenis instance so components (e.g. the scroll-to-top button)
  // can request scrolls through Lenis instead of native scrollTo, which fights it.
  (window as unknown as { __lenis?: LenisType }).__lenis = lenis;

  const update = (time: number) => {
    lenis.raf(time * 1000);
  };

  gsap.ticker.add(update);
  gsap.ticker.lagSmoothing(0);

  // --- Deep-link (initial hash) landing ---
  const initialId =
    window.location.hash.length > 1 ? decodeURIComponent(window.location.hash.slice(1)) : "";

  let settleRaf = 0;
  let settleCancelled = false;
  let finalized = false;
  let refreshBound = false;

  // Position the target under the header. lenis.resize() first so Lenis uses the
  // CURRENT (post-pin-expansion) page height, otherwise scrollTo clamps against a
  // stale, shorter height and lands on the footer / wrong spot.
  const placeTarget = () => {
    const target = document.getElementById(initialId);
    if (!target) {
      return false;
    }
    lenis.resize();
    lenis.scrollTo(target, { offset: -HEADER_OFFSET, immediate: true, force: true });
    return true;
  };
  // Re-assert on later layout refreshes (window load, late pin measures). Running
  // inside the refresh callback keeps it on the same frame the layout changes, so
  // there is no visible wrong-position frame.
  const onRefresh = () => {
    if (finalized && !settleCancelled) {
      placeTarget();
    }
  };
  const reveal = () => setCovering(false);

  const endInitialSettle = () => {
    if (settleCancelled) {
      return;
    }
    settleCancelled = true;
    if (settleRaf) {
      cancelAnimationFrame(settleRaf);
    }
    reveal();
  };

  if (initialId) {
    const start = Date.now();
    const minSettleMs = 700; // clear the +300ms pin refresh
    const giveUpAt = start + 4000; // never trap the veil / bad hash
    const stopRefreshWatchAt = start + 3000; // stop re-asserting after layout is done
    let seenAt = 0;
    let lastTop = Number.NaN;
    let stableFrames = 0;

    const step = () => {
      if (settleCancelled) {
        return;
      }
      const now = Date.now();
      const target = document.getElementById(initialId);

      if (!target) {
        if (now < giveUpAt) {
          settleRaf = requestAnimationFrame(step);
        } else {
          finalized = true;
          reveal(); // bad/missing hash, just show the page
        }
        return;
      }

      if (!seenAt) {
        seenAt = now;
      }
      const top = target.offsetTop;
      if (top === lastTop) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
        lastTop = top;
      }

      const layoutSettled = now - seenAt >= minSettleMs && stableFrames >= 5;
      if (layoutSettled || now > giveUpAt) {
        finalized = true;
        ScrollTrigger.refresh();
        placeTarget();
        ScrollTrigger.addEventListener("refresh", onRefresh);
        refreshBound = true;
        // Reveal after the scrolled position has painted (double rAF).
        requestAnimationFrame(() => requestAnimationFrame(reveal));
        // Stop the invisible re-assert once the layout is fully done.
        window.setTimeout(() => {
          if (refreshBound) {
            ScrollTrigger.removeEventListener("refresh", onRefresh);
            refreshBound = false;
          }
        }, Math.max(0, stopRefreshWatchAt - Date.now()));
        return;
      }
      settleRaf = requestAnimationFrame(step);
    };
    settleRaf = requestAnimationFrame(step);

    // If the user takes over (scrolls / keys) before we land, bail and reveal.
    window.addEventListener("wheel", endInitialSettle, { passive: true });
    window.addEventListener("touchstart", endInitialSettle, { passive: true });
    window.addEventListener("keydown", endInitialSettle);
  } else {
    setCovering(false);
  }

  return () => {
    settleCancelled = true;
    if (settleRaf) {
      cancelAnimationFrame(settleRaf);
    }
    if (refreshBound) {
      ScrollTrigger.removeEventListener("refresh", onRefresh);
    }
    window.removeEventListener("wheel", endInitialSettle);
    window.removeEventListener("touchstart", endInitialSettle);
    window.removeEventListener("keydown", endInitialSettle);
    gsap.ticker.remove(update);
    if ((window as unknown as { __lenis?: LenisType }).__lenis === lenis) {
      delete (window as unknown as { __lenis?: LenisType }).__lenis;
    }
    lenis.destroy();
    ScrollTrigger.refresh();
  };
}

export function SmoothScrollProvider({ children }: SmoothScrollProviderProps) {
  const reducedMotion = useReducedMotion();
  const smoothActive = useMemo(() => smoothScrollSupported(reducedMotion), [reducedMotion]);

  // When the page is opened/refreshed on a deep link (`/page#section`) with smooth
  // scroll active, we cover the viewport with a short veil while the lazy page +
  // GSAP pins finish measuring, then land on the section ONCE and reveal. This is
  // the only reliable way to avoid the flicker / footer / wrong-section jump that
  // comes from scrolling into a still-shifting, pinned layout.
  const [coverBg] = useState(() => {
    if (typeof window === "undefined") {
      return "#ffffff";
    }
    const bodyBg = getComputedStyle(document.body).backgroundColor;
    if (bodyBg && !/rgba?\(0,\s*0,\s*0,\s*0\)|transparent/.test(bodyBg)) {
      return bodyBg;
    }
    return document.documentElement.classList.contains("dark") ? "#0a1c2c" : "#ffffff";
  });
  const [covering, setCovering] = useState(
    () => smoothActive && typeof window !== "undefined" && window.location.hash.length > 1,
  );

  useEffect(() => {
    if (!smoothActive) {
      // Smooth scroll disabled (reduced motion / mobile / low-end): no Lenis, and
      // no veil to manage, native anchor scrolling handles hashes here. Lenis + gsap
      // are never imported on this path, so mobile never downloads them.
      setCovering(false);
      return;
    }

    // Desktop-only: pull Lenis + gsap + ScrollTrigger in dynamically so these ~90KB of
    // JS stay off the (mobile) critical path entirely. `cancelled` guards against the
    // component unmounting before the async chunk resolves.
    let cancelled = false;
    let teardown: (() => void) | null = null;

    void Promise.all([
      import("lenis"),
      import("gsap"),
      import("gsap/ScrollTrigger"),
    ]).then(([lenisModule, gsapModule, scrollTriggerModule]) => {
      if (cancelled) {
        return;
      }
      teardown = setupSmoothScroll(
        lenisModule.default,
        gsapModule.default,
        scrollTriggerModule.ScrollTrigger,
        setCovering,
      );
    });

    return () => {
      cancelled = true;
      teardown?.();
    };
  }, [smoothActive]);

  // In-page anchor clicks (`<a href="#section">`, e.g. every CTA that jumps to the
  // lead/enquiry form). Attached on ALL devices — scrollToElement uses Lenis when
  // it's active (desktop) and a self-correcting frame scroll otherwise (mobile),
  // so the jump is smooth and lands exactly on the form everywhere.
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const onAnchorClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const anchor = (event.target as HTMLElement | null)?.closest?.('a[href^="#"]') as HTMLAnchorElement | null;
      if (!anchor) {
        return;
      }
      const rawHash = anchor.getAttribute("href") ?? "";
      if (rawHash.length < 2) {
        return; // bare "#"
      }
      const target = document.getElementById(decodeURIComponent(rawHash.slice(1)));
      if (!target) {
        return;
      }
      event.preventDefault();
      scrollToElement(target);
      window.history.replaceState(null, "", rawHash);
    };
    document.addEventListener("click", onAnchorClick);
    return () => document.removeEventListener("click", onAnchorClick);
  }, []);

  return (
    <>
      {covering ? (
        <div
          aria-hidden
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2147483000,
            background: coverBg,
            pointerEvents: "none",
          }}
        />
      ) : null}
      {children}
    </>
  );
}
